import { useState, useCallback, memo } from 'react';
import { Switch } from '@mui/material';
import { CellContext } from '@tanstack/react-table';
import { NationalModifiersDetails } from '@/types/nationalModifiers.types';
import { CustomTableMeta } from '@/components/Tables/TableCard/TableCard.types';

export const SwitchCell = memo(({ row, table, column }: CellContext<NationalModifiersDetails, unknown>) => {
  const { original } = row;
  const { updateData } = table.options.meta as CustomTableMeta<NationalModifiersDetails>;
  const field = column.id as keyof NationalModifiersDetails;
  const initialChecked = Boolean(original?.[field] as unknown);
  const [checked, setChecked] = useState(initialChecked);

  const applyUpdate = useCallback(
    (nextValue: boolean) => {
      setChecked(nextValue);
      const updateFn = updateData as typeof updateData & {
        immediate?: (rowIndex: number, columnId: string, value: string | boolean) => void;
      };
      if (updateFn.immediate) {
        updateFn.immediate(row.index, column.id || '', nextValue);
      } else {
        updateData(row.index, column.id || '', nextValue);
      }
    },
    [updateData, row.index, column.id],
  );

  const handleClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      applyUpdate(!checked);
    },
    [applyUpdate, checked],
  );

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      event.stopPropagation();
      applyUpdate(event.target.checked);
    },
    [applyUpdate],
  );

  return (
    <Switch color="error" title="switch" checked={checked} onClick={handleClick} onChange={handleChange} size="small" />
  );
});

SwitchCell.displayName = 'SwitchCell';
